/**
 * TODAY'S CALLS, ON A MAP.
 *
 * The middle of the screen stopped being where screens live when the app
 * moved into a side panel, and this is the first thing that earns the
 * space it left: where the work is today. A shop that does mobile work
 * spends the morning answering "who is closest to that" out of memory, and
 * memory is worse at it than a map with eight dots on it.
 *
 * ── WHAT A "CALL" IS HERE ──────────────────────────────────────────────
 *
 * A job scheduled for today. Mobile jobs sit at the customer's town; shop
 * jobs sit at the shop, because "in the shop" is not a missing address —
 * it is the most precise one on the record. A job with no date is not
 * today's work and is not on this map.
 *
 * ── TOWN, NOT DOORSTEP, AND THE MAP SAYS SO ────────────────────────────
 *
 * Nothing in this database has a coordinate on it. What it has is
 * `jobs.location` and `customers.address`, which are free text somebody
 * typed, and the rules for reading a town out of those live in
 * src/domain/place.ts — the same rules the public service map uses, so the
 * two cannot disagree about where Hemet is.
 *
 * That means every pin is its TOWN CENTRE and not a driveway. Pins are
 * scattered around the centre so that eight jobs in Hemet read as eight
 * jobs rather than one, and the scatter is seeded off the job id so a pin
 * does not jump every time the page redraws. The card says "towns, not
 * exact addresses" out loud, because a map that looks like GPS and is not
 * is worse than no map.
 *
 * ── AND IT COUNTS WHAT IT COULD NOT PLACE ──────────────────────────────
 *
 * A place we do not recognise is reported, never guessed at. Nineteen jobs
 * once dropped off the public map in silence and it read as a quiet month
 * rather than a broken feed. The count of unplaced calls is on the card,
 * with what they actually said, so fixing one is a matter of adding a line
 * to city-centroids.json rather than reading the code.
 */

import { db, api } from "../data/client.ts";
import { esc } from "../ui/dom.ts";
import { shopDayStartIso, shopTime, shopZone } from "../ui/shopclock.ts";
import { place, scatter, localTableFits, IN_SHOP, type CentroidTable } from "../domain/place.ts";
import { shopAddress } from "../data/jobs.ts";
import { STATUS_LABEL, type Status } from "../data/jobs.ts";

/** The statuses that mean somebody is going somewhere. */
const ON_TODAY = ["scheduled", "in_progress", "completed", "invoiced", "paid"];

/**
 * JUST ENOUGH OF LEAFLET TO BE CHECKED.
 *
 * @types/leaflet is not a dependency and adding one for six calls would
 * put a typings package in the build for a map on one screen. This is the
 * surface this file actually touches, written out, so tsc still catches a
 * typo in it — `any` would not.
 */
export type LatLng = [number, number];
export interface LMarker {
  addTo(m: LMap): LMarker;
  bindPopup(html: string): LMarker;
  /** The Rentals map labels its pins in place. See src/pages/rentmap.ts. */
  bindTooltip(html: string, opts?: Record<string, unknown>): LMarker;
  on(ev: string, fn: () => void): LMarker;
  setStyle(s: { radius?: number; weight?: number }): void;
}
export interface LBounds { pad(n: number): LBounds }
export interface LMap {
  fitBounds(b: LBounds, opts?: { maxZoom?: number }): void;
}
export interface Leaflet {
  map(el: HTMLElement, opts: Record<string, unknown>): LMap;
  tileLayer(url: string, opts: Record<string, unknown>): { addTo(m: LMap): unknown };
  circleMarker(at: LatLng, opts: Record<string, unknown>): LMarker;
  featureGroup(ms: LMarker[]): { getBounds(): LBounds };
}

/** Leaflet, pinned, from a CDN. There is no bundler step for the client. */
const LEAFLET_JS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js";
const LEAFLET_CSS = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css";

/**
 * OPENSTREETMAP, NOT CARTO.
 *
 * CARTO's free raster tiles started rendering an "API KEY REQUIRED"
 * watermark across every tile in September with no console error and no
 * failed request, so the map looked broken and nothing reported it. Every
 * map in the network moved to OSM, which needs no key, plus a CSS filter
 * to take it down to a basemap that does not shout over the pins.
 */
export const TILES = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const TILE_CREDIT =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

type Call = {
  id: string;
  job_number: number | string;
  status: string;
  title: string | null;
  service_at: "mobile" | "shop" | null;
  location: string | null;
  scheduled_at: string | null;
  customers: { full_name: string; address: string | null } | null;
};

/**
 * The dot colors, by what is happening to the job — the SAME status→colour
 * the job tiles use (.jtile.s-* in app.css), named as tokens so the map reads
 * in the shop's palette and follows light/dark. Was a set of hardcoded hexes
 * (an off-brand purple among them) that matched nothing on the desk.
 */
/**
 * GREEN IS STILL OPEN, PURPLE IS CLOSED.
 *
 * Anthony, 2026-09-16: "green and purple dots for open or closed jobs".
 *
 * It used to be five colours across five statuses, which meant reading a
 * legend to answer the only question the day map is actually asked, which
 * is "what is left today". Two colours answer it from across the room.
 *
 * WHERE THE LINE IS DRAWN, and it is a judgement worth stating: a job is
 * OPEN until the work is finished. Scheduled and in progress are open.
 * Completed, invoiced and paid are closed.
 *
 * So `completed` reads closed even though the money has not arrived. On
 * this map that is right, because the question is where the van still has
 * to go. Whether the bill is paid is the money screen's question, not a
 * pin's, and colouring an unpaid job as "open" would send somebody back to
 * a driveway where the work is already done.
 */
const OPEN_STATUSES = new Set(["scheduled", "in_progress"]);
const DOT_OPEN = "--green";
const DOT_CLOSED = "--purple";
const DOT_FALLBACK = "--text3";

const dotFor = (status: string | null | undefined): string => {
  const s = String(status ?? "").toLowerCase();
  if (!s) return DOT_FALLBACK;
  return OPEN_STATUSES.has(s) ? DOT_OPEN : DOT_CLOSED;
};

/** A token's current value, for Leaflet — the canvas takes a real colour, not
 *  a `var(--x)`. Reads the live value so it honours the active theme. */
export function tokenColor(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#94a3b8";
}

let cities: CentroidTable | null = null;

/**
 * THE TOWN TABLE, ONCE.
 *
 * Served by the same route that serves the public map, off the same file
 * on disk, so there is exactly one list of towns in the product. A failure
 * here is reported rather than swallowed — an empty table places nothing,
 * and "no calls could be placed" with no reason is the tidy nothing this
 * app tries hardest not to render.
 */
async function townTable(): Promise<CentroidTable> {
  if (cities) return cities;
  const r = await fetch("/api/city-centroids");
  if (!r.ok) throw new Error(`The town list did not load (${r.status}).`);
  cities = await r.json() as CentroidTable;
  return cities;
}

/** Leaflet, loaded once, on the first map that needs it. */
let leafletReady: Promise<void> | null = null;
export function loadLeaflet(): Promise<void> {
  if (leafletReady) return leafletReady;
  leafletReady = new Promise<void>((resolve, reject) => {
    if ((window as { L?: unknown }).L) return resolve();
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = LEAFLET_CSS;
    document.head.appendChild(css);
    const js = document.createElement("script");
    js.src = LEAFLET_JS;
    js.async = true;
    js.onload = () => resolve();
    js.onerror = () => reject(new Error("The map library did not load."));
    document.head.appendChild(js);
  });
  return leafletReady;
}

/** The day's window, in the SHOP's zone rather than the browser's. */
function todayWindow(): { from: string; to: string } {
  const from = shopDayStartIso();
  const to = new Date(Date.parse(from) + 24 * 3600_000).toISOString();
  return { from, to };
}

async function todaysCalls(): Promise<Call[]> {
  const sb = await db();
  const { from, to } = todayWindow();
  /* No account filter. RLS answers "whose jobs", the same as everywhere
     else in this app — a client-side filter on account_id is a second
     answer to a question the database has already settled. */
  const { data, error } = await sb.from("jobs")
    .select("id,job_number,status,title,service_at,location,scheduled_at," +
            "customers(full_name,address)")
    .in("status", ON_TODAY)
    .gte("scheduled_at", from)
    .lt("scheduled_at", to)
    .order("scheduled_at", { ascending: true })
    .limit(200);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Call[];
}

/**
 * "In the shop" means THIS shop's address, not Supercanic's.
 *
 * place() reads "in the shop" as San Jacinto, which is right for the shop
 * it was written for and wrong for every other one: R&T's in-shop work in
 * Auburn, Alabama would have drawn in California. When the account has an
 * address on it, that address is the place.
 */
let shopAddr: string | null = null;
const whereText = (c: Call): string | null =>
  c.location && IN_SHOP.test(c.location) && shopAddr ? shopAddr : c.location;

const shopName = (c: Call) => c.customers?.full_name ?? "No customer on the job";

/**
 * The calls this browser could not place, placed by the server.
 *
 * Sends the job id and the best string we have for it, gets back a
 * coordinate for the ones it can name. The id is the job's own uuid, which
 * the caller already has on screen, so nothing identifying is created by
 * sending it back to the server it came from.
 */
async function placeLeftovers(
  calls: Call[],
): Promise<Map<string, { lat: number; lng: number; city: string | null }>> {
  const out = new Map<string, { lat: number; lng: number; city: string | null }>();
  const items = calls
    .map((c) => ({ id: c.id, text: whereText(c) ?? "", alt: c.customers?.address ?? "" }))
    .filter((i) => i.text.trim() || i.alt.trim());
  if (!items.length) return out;

  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  const r = await fetch(api("/api/place-leftovers"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
    },
    body: JSON.stringify({ items }),
  });
  if (!r.ok) return out;
  const body = await r.json() as {
    placed?: Array<{ id: string; lat: number; lng: number; city: string | null }>;
  };
  for (const p of body.placed ?? []) {
    if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) {
      out.set(p.id, { lat: p.lat, lng: p.lng, city: p.city ?? null });
    }
  }
  return out;
}

/**
 * DRAW IT.
 *
 * Takes the id of an empty element and fills it. Every failure lands in
 * the same card as a sentence somebody can act on, which is the only
 * reason this is one function and not three.
 */
export async function dayMap(intoId: string): Promise<void> {
  const host = document.getElementById(intoId);
  if (!host) return;
  host.innerHTML = `<div class="card daymap-card">
      <div class="card-head">Today on the road<span class="daymap-note">loading&hellip;</span></div>
      <div class="daymap-shell"><div class="loading">Finding today's calls&hellip;</div></div>
    </div>`;

  let calls: Call[];
  let table: CentroidTable;
  try {
    [calls, table, shopAddr] = await Promise.all([
      todaysCalls(), townTable(), shopAddress().catch(() => null)]);
  } catch (e) {
    host.innerHTML = `<div class="card daymap-card">
      <div class="card-head">Today on the road</div>
      <div class="pad"><p class="msg bad">${
        esc(e instanceof Error ? e.message : String(e))}</p></div></div>`;
    return;
  }

  /* The job's own location first, then the customer's address — eleven of
     September's jobs keep their town only on the customer. */
  const placed: Array<{ call: Call; at: [number, number]; city: string | null }> = [];
  let unplaced: Call[] = [];
  /* The local table is one corner of California. A shop somewhere else
     sends everything to the national placer instead of risking a street
     name being read as a California town. */
  const local = localTableFits(shopAddr);
  for (const c of calls) {
    const found = local
      ? place(whereText(c), table) ?? place(c.customers?.address ?? null, table)
      : null;
    if (!found) { unplaced.push(c); continue; }
    placed.push({ call: c, at: scatter(found.at, c.id), city: found.city });
  }

  /* ASK THE SERVER ABOUT THE ONES THIS TABLE HAS NO ROW FOR.
     The 79 towns above are Supercanic's own corner of California. A
     dispatch job in Columbus Ohio is not in them and never will be, and
     the full Census set is 3MB, which is not going into a page. So the
     leftovers go to /api/place-leftovers and the server answers from it.
     Failure here is deliberately silent: the map we already had is the
     worst case, not a broken one. */
  if (unplaced.length) {
    const asked = unplaced;
    try {
      const found = await placeLeftovers(asked);
      if (found.size) {
        const still: Call[] = [];
        for (const c of asked) {
          const hit = found.get(c.id);
          if (hit) placed.push({ call: c, at: scatter([hit.lat, hit.lng], c.id), city: hit.city });
          else still.push(c);
        }
        unplaced = still;
      }
    } catch { /* leave unplaced exactly as it was */ }
  }

  const when = new Date().toLocaleDateString("en-US",
    { timeZone: shopZone(), weekday: "long", month: "long", day: "numeric" });

  const rows = calls.map((c) => {
    const hit = placed.find((p) => p.call.id === c.id);
    return `<a class="daymap-row${hit ? "" : " off"}" href="#/job/${esc(c.id)}"
               data-job="${esc(c.id)}">
        <span class="daymap-time">${esc(shopTime(c.scheduled_at))}</span>
        <span class="daymap-dot" style="background:var(${dotFor(c.status)})"></span>
        <span class="daymap-who"><b>${esc(shopName(c))}</b>
          <i>${esc(c.title ?? `Job #${c.job_number}`)}</i></span>
        <span class="daymap-where">${
          hit ? esc(hit.city ?? "placed")
              : `<span class="daymap-nowhere">no town on it</span>`}</span>
      </a>`;
  }).join("");

  host.innerHTML = `<div class="card daymap-card">
      <div class="card-head">Today on the road
        <span class="daymap-note">${esc(when)} &middot; ${calls.length} call${
          calls.length === 1 ? "" : "s"}</span></div>
      <div class="daymap-shell">
        ${placed.length
          ? `<div class="daymap-map" id="daymap-canvas"></div>`
          : `<div class="daymap-blank">${calls.length
              ? "Nothing today could be placed on the map."
              : "Nothing is on the books for today."}</div>`}
        <div class="daymap-list">${rows || `<div class="daymap-blank sm">No calls today.</div>`}</div>
      </div>
      <div class="daymap-foot">
        <span class="daymap-key">
          <span class="daymap-dot" style="background:var(${DOT_OPEN})"></span>still open
          <span class="daymap-dot" style="background:var(${DOT_CLOSED})"></span>done
        </span>
        <span>Pins are TOWNS, not exact addresses.</span>
        ${unplaced.length
          ? `<span class="daymap-warn">${unplaced.length} call${
              unplaced.length === 1 ? "" : "s"} could not be placed: ${
              esc([...new Set(unplaced.map((c) =>
                c.location ?? c.customers?.address ?? "nothing written down"))]
                .slice(0, 3).map((s) => String(s).slice(0, 40)).join("; "))}</span>`
          : ""}
      </div>
    </div>`;

  if (!placed.length) return;

  try {
    await loadLeaflet();
  } catch (e) {
    const shell = host.querySelector(".daymap-map");
    if (shell) {
      shell.innerHTML = `<div class="daymap-blank">${
        esc(e instanceof Error ? e.message : String(e))} The list below is all of it.</div>`;
    }
    return;
  }

  const L = (window as unknown as { L: Leaflet }).L;
  const canvas = document.getElementById("daymap-canvas");
  if (!canvas) return;

  const map = L.map(canvas, { scrollWheelZoom: false, zoomControl: true, attributionControl: true });
  L.tileLayer(TILES, { attribution: TILE_CREDIT, maxZoom: 18 }).addTo(map);

  const marks = placed.map((p) => {
    const m = L.circleMarker(p.at, {
      radius: 8, weight: 2, color: tokenColor("--bg2"),
      fillColor: tokenColor(dotFor(p.call.status)), fillOpacity: 1,
    }).addTo(map);
    m.bindPopup(`<b>${esc(shopName(p.call))}</b><br>
      ${esc(p.call.title ?? `Job #${p.call.job_number}`)}<br>
      ${esc(shopTime(p.call.scheduled_at))} &middot; ${
        esc(STATUS_LABEL[p.call.status as Status] ?? p.call.status)}<br>
      <a href="#/job/${esc(p.call.id)}">Open the job &rarr;</a>`);
    m.on("click", () => highlight(p.call.id));
    return m;
  });

  /**
   * FIT TO THE WORK, WITH A FLOOR ON THE ZOOM.
   *
   * One call on the map means fitBounds has a zero-size box to fit, and
   * Leaflet answers that with zoom 18 — a street corner, which tells you
   * nothing about a town-level pin. maxZoom holds it at neighbourhood
   * scale so a one-job day and an eight-job day read the same way.
   */
  map.fitBounds(L.featureGroup(marks).getBounds().pad(0.25), { maxZoom: 12 });

  /* Hovering a row lifts its pin, so the list and the map are one thing. */
  host.querySelectorAll<HTMLElement>(".daymap-row").forEach((row) => {
    row.addEventListener("mouseenter", () => highlight(row.dataset["job"] ?? ""));
  });

  function highlight(id: string): void {
    placed.forEach((p, i) => {
      const on = p.call.id === id;
      marks[i]?.setStyle({ radius: on ? 12 : 8, weight: on ? 3 : 2 });
    });
    host?.querySelectorAll<HTMLElement>(".daymap-row").forEach((r) =>
      r.classList.toggle("on", r.dataset["job"] === id));
  }
}
